import { useCallback } from 'react';
import type { MutableRefObject, ReactElement, ReactNode } from 'react';
import { useOverlayScrollbar } from '../DataTable/useOverlayScrollbar';
import type { OverlayScrollbar } from '../DataTable/useOverlayScrollbar';
import type { OverlayScrollAxis } from '../DataTable/overlayScrollAxis';
import { useWheelToHorizontalScroll } from '../hooks/useWheelToHorizontalScroll';

export type OverlayScrollAreaAxis = OverlayScrollAxis | 'both';

// Same thumb styling as the DataTable's rows region, so every overlay scrollbar looks identical.
const THUMB_CLASS: Readonly<Record<OverlayScrollAxis, string>> = {
    y: 'absolute right-0.5 top-0 z-10 w-1.5 touch-none rounded-full bg-gray-300 hover:bg-gray-400 transition-opacity duration-200',
    x: 'absolute bottom-0.5 left-0 z-10 h-1.5 touch-none rounded-full bg-gray-300 hover:bg-gray-400 transition-opacity duration-200'
};

const SCROLL_REGION_CLASS: Readonly<Record<OverlayScrollAreaAxis, string>> = {
    y: 'h-full overflow-y-auto overflow-x-hidden scrollbar-hide',
    x: 'overflow-x-auto overflow-y-hidden scrollbar-hide',
    both: 'h-full overflow-auto scrollbar-hide'
};

// A horizontal strip sizes to its children; a two-axis area fills its parent and widens only
// past its content's minimum, so the growth observer sees both.
const INNER_BASE_CLASS: Readonly<Record<OverlayScrollAreaAxis, string>> = {
    y: '',
    x: 'w-max min-w-full',
    both: 'min-w-fit'
};

function innerClassFor(axis: OverlayScrollAreaAxis, innerClassName?: string): string | undefined {
    const classes = [INNER_BASE_CLASS[axis], innerClassName].filter(Boolean).join(' ');
    return classes || undefined;
}

/** The overlay thumb alone, for surfaces that own their scroll element (e.g. PDFViewer). */
export function OverlayScrollThumb({ overlay }: { overlay: OverlayScrollbar }) {
    return (
        <div
            ref={overlay.thumbRef}
            aria-hidden="true"
            {...overlay.thumbHandlers}
            className={`${THUMB_CLASS[overlay.axis]} ${
                overlay.hasOverflow && overlay.isVisible
                    ? 'opacity-100'
                    : 'opacity-0 pointer-events-none'
            }`}
        />
    );
}

export interface OverlayScrollAreaProps {
    children: ReactNode;
    /** `'x'` makes a horizontal strip (tab bars, chip rows); `'both'` scrolls wide tables too. */
    axis?: OverlayScrollAreaAxis;
    /** With `axis="x"`, a vertical mouse wheel scrolls the strip sideways. Off by default. */
    wheelToHorizontal?: boolean;
    /** Classes for the relative wrapper - how the area sizes in its parent. */
    className?: string;
    /** Classes for the scroll region itself (padding, gap, etc.). */
    contentClassName?: string;
    /** Classes for the observed inner wrapper - e.g. `min-h-full flex flex-col`. */
    innerClassName?: string;
    /** Inset at both ends of the track - raise it inside rounded cards. */
    trackInsetPx?: number;
    /** Populated with the scroll region, for callers that scroll or query inside it. */
    scrollRef?: MutableRefObject<HTMLDivElement | null>;
}

/** Scroll region with the shared overlay thumb - same hook as DataTable. */
export function OverlayScrollArea({
    children,
    axis = 'y',
    wheelToHorizontal = false,
    className,
    contentClassName,
    innerClassName,
    trackInsetPx,
    scrollRef
}: OverlayScrollAreaProps): ReactElement {
    const scrollsY = axis !== 'x';
    const scrollsX = axis !== 'y';
    // Both hooks always run (hook order); an axis the area doesn't scroll never gets an element.
    const yOverlay = useOverlayScrollbar({ trackInsetPx, axis: 'y' });
    const xOverlay = useOverlayScrollbar({ trackInsetPx, axis: 'x' });
    const { setScrollElement: setYElement } = yOverlay;
    const { setScrollElement: setXElement } = xOverlay;
    useWheelToHorizontalScroll(axis === 'x' && wheelToHorizontal ? xOverlay.scrollElement : null);

    const attachScrollElement = useCallback(
        (node: HTMLDivElement | null) => {
            if (scrollRef) scrollRef.current = node;
            if (scrollsY) setYElement(node);
            if (scrollsX) setXElement(node);
        },
        [scrollRef, scrollsY, scrollsX, setYElement, setXElement]
    );

    return (
        <div className={`relative ${className ?? ''}`}>
            <div
                ref={attachScrollElement}
                className={`${SCROLL_REGION_CLASS[axis]} ${contentClassName ?? ''}`}
            >
                {/* Single child: the hook observes firstElementChild for growth. */}
                <div className={innerClassFor(axis, innerClassName)}>{children}</div>
            </div>
            {/* Presentational only - keyboard users scroll the region natively. */}
            {scrollsY && <OverlayScrollThumb overlay={yOverlay} />}
            {scrollsX && <OverlayScrollThumb overlay={xOverlay} />}
        </div>
    );
}
